import { DARK_TONE, stepKey } from "./process-step";
import type { ProcessStep } from "./process-step";
import { cn } from "../../lib/utils";

/** The bold steps: what the list is made from and where it sits. */
interface BoldStepListProps {
  steps: ProcessStep[];
  layout: "vertical" | "horizontal" | "numbered";
  /** A colour the owner set for the numbers, over the theme's. */
  accentColor?: string;
  /** Whether the list sits on the dark band. */
  onDark: boolean;
}

/** A row of three is the usual process; it gets a single row as soon as a tablet fits it. */
const THREE_STEPS = 3,

  /**
   * A step's number as display type, with a short rule under it. On the dark
   * band the numbers carry the accent, the way the hero's buttons do.
   * @param {object} props The numeral.
   * @param {ProcessStep} props.step The step.
   * @param {boolean} props.onDark Whether it sits on the dark band.
   * @param {string} [props.accentColor] The owner's colour for it, if any.
   * @returns {React.JSX.Element} The numeral.
   */
  BoldNumeral = ({ step, onDark, accentColor }: { step: ProcessStep; onDark: boolean; accentColor?: string }): React.JSX.Element => (
    <div className="jobsite:shrink-0">
      <span
        className={cn(
          "jobsite:block jobsite:font-heading jobsite:text-5xl jobsite:leading-none jobsite:text-foreground jobsite:sm:text-6xl",
          onDark && "jobsite:text-accent jobsite:sm:text-7xl",
        )}
        style={(accentColor ?? "") === "" ? undefined : { color: accentColor }}
      >
        {step.step}
      </span>
      <span aria-hidden="true" className={cn("jobsite:mt-3 jobsite:block jobsite:h-1 jobsite:w-10 jobsite:bg-accent", onDark && DARK_TONE.line)} />
    </div>
  ),

  /**
   * A step's title and description in the bold style.
   * @param {object} props The text.
   * @param {ProcessStep} props.step The step.
   * @param {boolean} props.onDark Whether it sits on the dark band.
   * @returns {React.JSX.Element} The text.
   */
  BoldStepText = ({ step, onDark }: { step: ProcessStep; onDark: boolean }): React.JSX.Element => (
    <div className="jobsite:min-w-0 jobsite:pt-1 jobsite:sm:pt-0">
      <h3 className={cn("jobsite:font-heading jobsite:text-xl jobsite:leading-tight jobsite:text-foreground", onDark && DARK_TONE.title)}>{step.title}</h3>
      <p className={cn("jobsite:mt-2 jobsite:text-base jobsite:leading-relaxed jobsite:text-muted-foreground", onDark && DARK_TONE.body)}>{step.description}</p>
    </div>
  ),

  /**
   * The steps in the bold style: an ordered list whose numbers are display
   * type. The layout still decides the arrangement: a row of columns, a stack,
   * or a stack of cards. In a row, a phone sets each number beside its step so
   * three steps fit a screen; from tablet width up three stand in one row.
   * @param {BoldStepListProps} props The list.
   * @returns {React.JSX.Element} The list.
   */
  BoldStepList = ({ steps, layout, accentColor, onDark }: BoldStepListProps): React.JSX.Element => {
    if (layout === "horizontal") {
      return (
        <ol
          className={cn(
            "jobsite:grid jobsite:grid-cols-1 jobsite:gap-8 jobsite:sm:grid-cols-2 jobsite:sm:gap-10 jobsite:lg:grid-cols-3 jobsite:lg:gap-12",
            steps.length === THREE_STEPS && "jobsite:sm:grid-cols-3",
          )}
        >
          {steps.map((step) => (
            <li key={stepKey(step)} className="jobsite:flex jobsite:gap-5 jobsite:sm:block jobsite:sm:space-y-5">
              <BoldNumeral step={step} onDark={onDark} accentColor={accentColor} />
              <BoldStepText step={step} onDark={onDark} />
            </li>
          ))}
        </ol>
      );
    }
    return (
      <ol className={cn("jobsite:max-w-3xl", layout === "numbered" ? "jobsite:space-y-4" : "jobsite:space-y-10")}>
        {steps.map((step) => (
          <li
            key={stepKey(step)}
            className={cn(
              "jobsite:flex jobsite:gap-6",
              layout === "numbered" && "jobsite:border-2 jobsite:border-border jobsite:bg-card jobsite:p-6 jobsite:text-card-foreground",
              layout === "numbered" && onDark && DARK_TONE.card,
            )}
            style={layout === "numbered" ? { borderRadius: "var(--jobsite-radius)" } : undefined}
          >
            <BoldNumeral step={step} onDark={onDark} accentColor={accentColor} />
            <BoldStepText step={step} onDark={onDark} />
          </li>
        ))}
      </ol>
    );
  };

export { BoldStepList };
